import React, { useState, useEffect } from 'react';
import api, { extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function SqlConsole() {
  const [sql, setSql] = useState('');
  const [tables, setTables] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [loadingTables, setLoadingTables] = useState(false);

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const response = await api.get('/admin/sql-console/tables');
      if (response.data.success) {
        setTables(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching tables:', err);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleExecuteQuery = async () => {
    if (!sql.trim()) {
      setError('Por favor ingrese una consulta SQL');
      setErrorTrace('');
      setDebugInfo(null);
      return;
    }

    setLoading(true);
    setError('');
    setErrorTrace('');
    setDebugInfo(null);
    setResult(null);

    try {
      const response = await api.post('/admin/sql-console/execute', { sql });

      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || 'Error al ejecutar la consulta');
        setErrorTrace(response.data.errorTrace || '');
      }
    } catch (err) {
      console.error('Error executing query:', err);
      const errorDetails = extractErrorDetails(err, 'Error al ejecutar la consulta');
      setError(errorDetails.message);
      setErrorTrace(errorDetails.errorTrace || '');
      setDebugInfo(errorDetails.debugInfo);
    } finally {
      setLoading(false);
    }
  };

  const handleTableClick = (tableName) => {
    setSql(`SELECT * FROM ${tableName} LIMIT 100;`);
    setError(''); // Clear any previous errors
    setErrorTrace(''); // Clear error trace
    setDebugInfo(null); // Clear debug info
    setResult(null); // Clear previous results
  };

  const renderResult = () => {
    if (!result) return null;

    if (result.type === 'SELECT') {
      if (result.rows.length === 0) {
        return <p className="no-results">La consulta no devolvió resultados.</p>;
      }

      return (
        <div className="result-container">
          <p className="result-info">{result.rowCount} fila(s) retornadas</p>
          <div className="table-wrapper">
            <table className="result-table">
              <thead>
                <tr>
                  {result.columns.map((col, idx) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {result.columns.map((col, colIdx) => (
                      <td key={colIdx}>
                        {row[col] !== null && row[col] !== undefined 
                          ? String(row[col]) 
                          : <span className="null-value">NULL</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (result.type === 'UPDATE') {
      return (
        <div className="result-container">
          <p className="result-info success">
            ✓ Consulta ejecutada exitosamente. {result.affectedRows} fila(s) afectada(s).
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="sql-console-container">
      <h2>Consola SQL</h2>
      
      <div className="console-layout">
        <div className="main-area">
          <div className="query-section">
            <label htmlFor="sqlQuery">Consulta SQL</label>
            <textarea
              id="sqlQuery"
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              placeholder="Ingrese su consulta SQL aquí...&#10;Ejemplo: SELECT * FROM users LIMIT 10;"
              rows={8}
            />
            
            <button 
              onClick={handleExecuteQuery} 
              className="btn btn-primary"
              disabled={loading || !sql.trim()}
            >
              {loading ? 'Ejecutando...' : '▶ Ejecutar'}
            </button>
          </div>

          {error && (
            <ErrorDisplay 
              error={error} 
              errorTrace={errorTrace}
              debugInfo={debugInfo}
              className="sql-error-display"
            />
          )}

          <div className="results-section">
            {renderResult()}
          </div>
        </div>

        <div className="sidebar">
          <h3>Tablas del Sistema</h3>
          {loadingTables ? (
            <p>Cargando tablas...</p>
          ) : tables.length > 0 ? (
            <>
              <p className="sidebar-hint">Haga clic en una tabla para generar una consulta SELECT</p>
              <ul className="tables-list">
                {tables.map((table, idx) => (
                  <li key={idx} onClick={() => handleTableClick(table)} title={`SELECT * FROM ${table}`}>
                    📋 {table}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="no-tables">No se encontraron tablas</p>
          )}
        </div>
      </div>

      <style jsx>{`
        .sql-console-container {
          padding: 20px;
          max-width: 1600px;
          margin: 0 auto;
        }

        .console-layout {
          display: flex;
          gap: 20px;
          margin-top: 20px;
        }

        .main-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sidebar {
          width: 250px;
          min-height: 300px;
          max-height: 60vh;
          overflow-y: auto;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 15px;
          background: #f8f9fa;
        }

        .sidebar h3 {
          margin-top: 0;
          margin-bottom: 10px;
          font-size: 16px;
        }

        .sidebar-hint {
          font-size: 12px;
          color: #666;
          margin-bottom: 10px;
          font-style: italic;
        }

        .no-tables {
          font-size: 13px;
          color: #999;
          text-align: center;
          padding: 20px;
        }

        .tables-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .tables-list li {
          padding: 8px;
          cursor: pointer;
          border-radius: 4px;
          margin-bottom: 5px;
          transition: background 0.2s;
        }

        .tables-list li:hover {
          background: #e9ecef;
        }

        .query-section {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .query-section label {
          font-weight: 600;
          font-size: 14px;
        }

        .query-section textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          resize: vertical;
        }

        .results-section {
          min-height: 200px;
        }

        .result-container {
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
        }

        .result-info {
          padding: 12px;
          background: #e9ecef;
          margin: 0;
          font-weight: 600;
          border-bottom: 1px solid #ddd;
        }

        .result-info.success {
          background: #d4edda;
          color: #155724;
        }

        .table-wrapper {
          overflow: auto;
          max-height: 500px;
        }

        .result-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .result-table th {
          position: sticky;
          top: 0;
          background: #f8f9fa;
          padding: 10px;
          text-align: left;
          border-bottom: 2px solid #ddd;
          font-weight: 600;
        }

        .result-table td {
          padding: 8px 10px;
          border-bottom: 1px solid #eee;
        }

        .result-table tr:hover {
          background: #f8f9fa;
        }

        .null-value {
          color: #999;
          font-style: italic;
        }

        .no-results {
          padding: 40px;
          text-align: center;
          color: #666;
        }

        .sql-error-display {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 4px;
          border: 1px solid #f5c6cb;
        }
        
        .sql-error-display .error-message {
          margin: 0;
          white-space: pre-wrap;
        }

        @media (max-width: 1024px) {
          .console-layout {
            flex-direction: column;
          }

          .sidebar {
            width: 100%;
            height: auto;
            max-height: 200px;
          }
        }
      `}</style>
    </div>
  );
}

export default SqlConsole;
